if (!customElements.get('why-section-carousel')) {
  class WhySectionCarousel extends HTMLElement {
    constructor() {
      super();

      this.handleResize = this.handleResize.bind(this);
      this.setEqualHeights = this.setEqualHeights.bind(this);
      this.currentCellsPerGroup = null;
      this.currentVisibleCells = null;
      this.equalHeightsFrame = null;
      this.equalHeightsTimeout = null;
      this.initWhenVisible();
    }

    initWhenVisible() {
      if (parseInt(this.dataset.blockCount, 10) <= 1) {
        return;
      }

      theme.initWhenVisible({
        element: this,
        callback: this.init.bind(this),
        threshold: 300
      });
    }

    init() {
      this.slider = this.querySelector('.uagb-post__items');
      if (!this.slider) return;

      this.setupSlider();
      window.addEventListener('resize', this.handleResize);

      if (document.fonts && typeof document.fonts.ready?.then === 'function') {
        document.fonts.ready.then(() => {
          this.scheduleEqualHeights();
        });
      }
    }

    getCellsPerGroup() {
      if (window.innerWidth <= 767) return 1;
      if (window.innerWidth <= 1024) return 2;
      return 3;
    }

    getVisibleCells() {
      if (window.innerWidth <= 767) return 2;
      if (window.innerWidth <= 1024) return 2.15;
      return 3.5;
    }

    getGap() {
      return window.innerWidth <= 767 ? 14 : 20;
    }

    applyCellWidths() {
      const cellsPerGroup = this.getCellsPerGroup();
      const visibleCells = this.getVisibleCells();
      const gap = this.getGap();
      const carouselStyles = window.getComputedStyle(this);
      const paddingLeft = parseFloat(carouselStyles.paddingLeft) || 0;
      const paddingRight = parseFloat(carouselStyles.paddingRight) || 0;
      const sliderWidth = this.getBoundingClientRect().width - paddingLeft - paddingRight;
      const gapsCount = Math.max(0, Math.ceil(visibleCells) - 1);
      const cellWidth = (sliderWidth - gap * gapsCount) / visibleCells;
      const cells = this.querySelectorAll('.uagb-post__inner-wrap');

      cells.forEach((cell) => {
        cell.style.width = `${cellWidth}px`;
        cell.style.marginRight = `${gap}px`;
      });

      return {
        cellsPerGroup,
        visibleCells
      };
    }

    setupSlider() {
      const { cellsPerGroup, visibleCells } = this.applyCellWidths();

      if (this.flickity && typeof this.flickity.destroy === 'function') {
        this.flickity.destroy();
      }

      this.currentCellsPerGroup = cellsPerGroup;
      this.currentVisibleCells = visibleCells;

      this.flickity = new Flickity(this.slider, {
        accessibility: false,
        cellSelector: '.uagb-post__inner-wrap',
        cellAlign: 'left',
        contain: true,
        groupCells: window.innerWidth <= 767 ? 1 : cellsPerGroup,
        pageDots: true,
        prevNextButtons: window.innerWidth > 767,
        rightToLeft: theme.config.rtl,
        setGallerySize: true,
        wrapAround: false
      });

      this.scheduleEqualHeights();
    }

    handleResize() {
      const nextCellsPerGroup = this.getCellsPerGroup();
      const nextVisibleCells = this.getVisibleCells();
      this.applyCellWidths();

      if (nextCellsPerGroup !== this.currentCellsPerGroup || nextVisibleCells !== this.currentVisibleCells) {
        this.setupSlider();
      } else if (this.flickity && typeof this.flickity.resize === 'function') {
        this.flickity.resize();
        this.scheduleEqualHeights();
      }
    }

    scheduleEqualHeights() {
      if (this.equalHeightsFrame) {
        window.cancelAnimationFrame(this.equalHeightsFrame);
      }

      if (this.equalHeightsTimeout) {
        window.clearTimeout(this.equalHeightsTimeout);
      }

      this.equalHeightsFrame = window.requestAnimationFrame(() => {
        this.setEqualHeights();
        this.equalHeightsTimeout = window.setTimeout(this.setEqualHeights, 140);
      });
    }

    setEqualHeights() {
      const cells = Array.from(this.querySelectorAll('.uagb-post__inner-wrap'));

      if (!cells.length) return;

      cells.forEach((cell) => {
        cell.style.minHeight = '';
      });

      let maxHeight = 0;

      cells.forEach((cell) => {
        maxHeight = Math.max(maxHeight, cell.offsetHeight);
      });

      if (!maxHeight) return;

      cells.forEach((cell) => {
        cell.style.minHeight = `${maxHeight}px`;
      });

      if (this.flickity && typeof this.flickity.resize === 'function') {
        this.flickity.resize();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);

      if (this.equalHeightsFrame) {
        window.cancelAnimationFrame(this.equalHeightsFrame);
      }

      if (this.equalHeightsTimeout) {
        window.clearTimeout(this.equalHeightsTimeout);
      }

      if (this.flickity && typeof this.flickity.destroy === 'function') {
        this.flickity.destroy();
      }
    }
  }

  customElements.define('why-section-carousel', WhySectionCarousel);
}
